// Сеанс в интерфейсе: кто вошёл, его роли и права, администраторы, сборка и пояс установки —
// ответ GET /api/auth/me. Меню и «Создать ▾» строятся по нему; сервер всё равно проверяет
// каждое действие (FR-012).
// 031 (FR-028; ASG-NTF-06): у учётной записи нет своего пояса для писем — каркас один раз
// отправляет пояс браузера (useBrowserTimeZone).
import type { MeResponse } from '@kp/contracts';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import { api, apiKey, apiQuery } from '../api/client.ts';
import { commitAllDeferred, dropAllDeferred } from '../app/deferred.ts';
import { useErrorToast } from '../app/errors.ts';
import { DEFAULT_TIME_ZONE } from '../app/format.ts';
import { dropAllDrafts } from '../features/minutes/drafts.ts';
import { dropUserQueue, unsentOfUser } from '../features/minutes/queue/queue.ts';
import { dropOutbox, outboxCount } from '../features/my-items/outbox.ts';

export const meQueryKey = apiKey('auth.me');

/** Права меняются на ходу (роль снята, срок роли закончился) — меню свежит каждый переход. */
export function meQuery() {
  return { ...apiQuery('auth.me'), staleTime: 30_000 };
}

export function useMe(): MeResponse {
  return useSuspenseQuery(meQuery()).data;
}

export function useTimeZone(): string {
  return useMe().timeZone || DEFAULT_TIME_ZONE;
}

/** Пояс браузера (IANA): «Asia/Novosibirsk»; браузер его не знает — null. */
export function browserTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === 'string' && zone !== '' ? zone : null;
  } catch {
    return null;
  }
}

/** Учётные записи, чей пояс эта страница уже отправила: запрос — один раз. */
const sentTimeZones = new Set<string>();

/** Для тестов: страница словно открыта заново. */
export function forgetSentTimeZones(): void {
  sentTimeZones.clear();
}

/**
 * 031 (FR-028; ASG-NTF-06): письма о пунктах уходят в 08:00 по поясу человека. Пояс учётной
 * записи берётся из браузера при первом входе: auth.me ответил userTimeZone: null — каркас один
 * раз отправляет пояс браузера (auth.timeZone). И в сеансе по ссылке: письма нужны ответственному.
 * Запрос фоновый: его никто не просил, сеанс он не продлевает и окно «Войдите снова» не открывает.
 * Ошибка молча пропускается: письма уходят по поясу установки, следующий сеанс попробует снова.
 * Сервер записывает пояс, только если его ещё нет.
 */
export function useBrowserTimeZone(): void {
  const me = useMe();
  const userId = me.user.id;
  const missing = me.userTimeZone === null;
  useEffect(() => {
    if (!missing || sentTimeZones.has(userId)) return;
    const timeZone = browserTimeZone();
    if (!timeZone) return;
    sentTimeZones.add(userId);
    api('auth.timeZone', { body: { timeZone }, background: true }).catch(() => undefined);
  }, [missing, userId]);
}

/** Пример почты на корпоративном домене: «morozov@example.ru». */
export function useMailDomain(): string | undefined {
  return useMe().domains?.[0];
}

/** Перечитать сеанс: после смены ролей, входа и выхода. */
export function useRefreshMe(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(() => queryClient.invalidateQueries({ queryKey: meQueryKey }), [queryClient]);
}

/**
 * Сеанс завершён: данные прежнего сеанса стираются из памяти страницы, отложенные изменения,
 * которые ещё ждут своего тоста, отбрасываются — следующим сеансом они не уходят (T121), переход
 * на вход. Так заканчивается «Выйти»; «Выйти на всех устройствах» в профиле заканчивается тем же.
 */
/**
 * 009: своя почта сменилась — сеанс завершён, вход с новой почтой. Почта живёт только в памяти
 * вкладки до входа: в адрес страницы и хранилище браузера не попадает (принцип IV).
 */
let changedEmail: string | null = null;

/** Новая почта для страницы входа после смены своей почты; иначе null. */
export function changedEmailForLogin(): string | null {
  return changedEmail;
}

/** Вход прошёл — пояснение о смене почты больше не нужно. */
export function forgetChangedEmail(): void {
  changedEmail = null;
}

/**
 * Сеанс завершён: данные сеанса стираются, переход на вход. newEmail — своя почта сменилась
 * (009): страница входа подставит её и объяснит, почему нужно войти снова.
 */
export function useEndSession(): (options?: { newEmail?: string }) => Promise<void> {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useCallback(
    async (options?: { newEmail?: string }) => {
      changedEmail = options?.newEmail ?? null;
      dropAllDeferred();
      // Черновики таблицы протокола принадлежат сеансу (011, security CHK017).
      dropAllDrafts();
      queryClient.clear();
      await navigate({ to: '/login' });
    },
    [navigate, queryClient],
  );
}

/**
 * Неотправленные правки таблиц протокола вошедшего пользователя (015: FR-021; COLLAB-11): окно
 * «Выйти и потерять правки?» называет их число. Записи другого пользователя этого браузера не
 * считаются. 030 (FR-030; research Р9): и ответы «Моих задач», которые ждут сети.
 */
export function useUnsentEdits(): () => Promise<number> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    const me = queryClient.getQueryData<MeResponse>(meQueryKey);
    return me ? (await unsentOfUser(me.user.id)) + outboxCount(me.user.id) : 0;
  }, [queryClient]);
}

/**
 * «Выйти»: сначала уходят отложенные изменения — тем же сеансом, пока он действует (удаление
 * выгрузки, T121); затем сеанс завершается на сервере и — useEndSession. Сеанс уже закончился
 * (AUTH-004) — выходить не из чего: клиент API не открывает окно «Войдите снова» и завершает
 * выход без ошибки (FR-009, FR-011а). Не удалось (нет связи, сбой) — тост с «Повторить»: cookie
 * сеанса осталась, и переход на вход с тем же сеансом ничего бы не завершил.
 *
 * 015 (FR-021; ADR-0032): выход подтверждён (окно с числом правок — useUnsentEdits) —
 * неотправленные команды пользователя в браузере удаляются; окончание сеанса без выхода
 * (useEndSession, «Войдите снова») их не трогает. Таблица протокола закрывается после ухода со
 * страницы и пишет неотправленное в черновики — после неё черновики и очередь стираются ещё раз.
 */
export function useLogout(): () => Promise<void> {
  const endSession = useEndSession();
  const showError = useErrorToast();
  const queryClient = useQueryClient();
  return useCallback(
    async function logout(): Promise<void> {
      await commitAllDeferred();
      try {
        await api('auth.logout');
      } catch (error) {
        showError(error, () => void logout());
        return;
      }
      const userId = queryClient.getQueryData<MeResponse>(meQueryKey)?.user.id ?? null;
      if (userId) await dropUserQueue(userId);
      // 030 (research Р9): выход подтверждён — неотправленные ответы «Моих задач» удаляются.
      if (userId) dropOutbox(userId);
      await endSession();
      if (!userId) return;
      window.setTimeout(() => {
        dropAllDrafts();
        void dropUserQueue(userId);
      }, 0);
    },
    [endSession, showError, queryClient],
  );
}
